import { useQuery } from '@tanstack/react-query'
import { get, ApiError, type ApiSchema } from '@wp-one/api'
import { MODULES, ALWAYS_ALLOWED, type ModuleId } from './modules'
import { SESSION_QUERY_KEY } from './keys'
import { recordUnauthorized } from './signOut'

export { SESSION_QUERY_KEY }

/**
 * GET /auth/me, typed from the API contract (`pnpm api:tipos`). Payload fields follow the
 * API's (Portuguese) naming. The front only obeys `modulos`/`secoes`: it never infers
 * access from the profile. `secoes` are 'one:agenda', 'carreiras:estagios'... (in the ONE, seduc
 * only gets one:inicio, one:agenda, one:documentos and one:ava).
 */
export type SessionUser = Omit<ApiSchema<'AuthMeResponse'>, 'modulos'> & {
  /** Narrowed to the modules the front knows; unknown ones are dropped by allowedModules. */
  modulos: ModuleId[]
}

/** Current session. `null` = not authenticated (401). */
export function useSession() {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async (): Promise<SessionUser | null> => {
      try {
        return await get<SessionUser>('/auth/me')
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          recordUnauthorized(error)
          return null
        }
        throw error
      }
    },
    staleTime: 60_000,
  })
}

export function allowedModules(user: SessionUser | null): ModuleId[] {
  const fromApi = user?.modulos.filter((m) => MODULES.includes(m)) ?? []
  return [...new Set([...fromApi, ...ALWAYS_ALLOWED])]
}

export function canAccess(user: SessionUser | null, module: ModuleId): boolean {
  return allowedModules(user).includes(module)
}

/** Whether the profile can see a section inside a module (e.g. 'one:cursos'). The API decides; the front obeys. */
export function canSeeSection(user: SessionUser | null, section: string): boolean {
  return user?.secoes.includes(section) ?? false
}
